import { Box, createStyles, Group, Stack } from '@mantine/core';
import {
	Selector,
	SelectorTarget,
	SelectType,
	SettingsBlock,
	SettingsSelect,
} from '@repo/common/components/index.ts';
import { pluralize } from '@repo/common/utils/stringUtils.ts';
import { Text, Title } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import {
	useUpdateIntegration,
	useUpdateUser,
	useUserList,
	type IIntegration,
} from '../../../../../api';
import { useFuseSearchCallback } from '../../../../../hooks/useFuseSearchCallback.ts';
import { getHash } from '../../../../../utils/shared.utils.ts';

const useStyles = createStyles((theme) => ({
	button: {
		'& .mantine-Button-inner': {
			paddingRight: theme.spacing['3xs'],
			paddingTop: theme.spacing['3xs'],
			paddingBottom: theme.spacing['3xs'],
			paddingLeft: theme.spacing.xs,
		},
		marginRight: -4,
		height: 'auto',
		padding: 0,
	},
}));

const LOOKBACK_OPTIONS = [
	{ value: '1', label: '1 day' },
	{ value: '7', label: '7 days' },
	{ value: '30', label: '30 days' },
];

interface PopularityProps {
	integration: IIntegration;
}

export function Popularity({ integration }: PopularityProps) {
	const { classes } = useStyles();

	const { data: serviceAccounts } = useUserList({
		filters: {
			is_service_account: true,
		},
		options: {
			select: ({ results }) => results,
		},
	});

	const { mutateAsync: updateIntegration } = useUpdateIntegration({});
	const { mutateAsync: updateUser } = useUpdateUser({});
	const [lookbackDays, setLookbackDays] = useState(
		integration.credentials?.popularity_lookback_days ?? '7'
	);

	const handlePopularityChange = useCallback(
		(hashes: string[]) => {
			const selectedUsers =
				serviceAccounts?.filter((user) =>
					hashes.includes(getHash(user.email).toString())
				) ?? [];
			const unselectedUsers =
				serviceAccounts
					?.filter((user) => user.service_account_count_popularity)
					.filter((user) => !hashes.includes(getHash(user.email).toString())) ??
				[];

			for (const user of selectedUsers) {
				if (user.service_account_count_popularity) {
					// no need to update the user if it's already selected

					// eslint-disable-next-line no-continue
					continue;
				}
				updateUser({
					data: { id: user.id, service_account_count_popularity: true },
				});
			}

			for (const user of unselectedUsers) {
				updateUser({
					data: { id: user.id, service_account_count_popularity: false },
				});
			}
		},
		[serviceAccounts, updateUser]
	);

	const handlePopularityLookbackChange = useCallback(
		(value: string) => {
			setLookbackDays(value);
			const credentials = {
				...(integration?.credentials ?? {}),
				popularity_lookback_days: value,
			};
			updateIntegration({
				data: { id: integration.id, credentials },
			});
		},
		[updateIntegration, integration, setLookbackDays]
	);

	const getFilteredServiceAccounts = useFuseSearchCallback({
		items: serviceAccounts ?? [],
		keys: ['email'],
		options: { threshold: 0.3 },
	});

	const getFilteredServiceAccountsItems = useCallback(
		async (searchTerm: string) => {
			const filteredServiceAccounts = getFilteredServiceAccounts(searchTerm);
			return filteredServiceAccounts.map((user) => {
				const emailHash = getHash(user.email).toString();
				return {
					value: emailHash,
					label: user.email,
				};
			});
		},
		[getFilteredServiceAccounts]
	);

	const usersValues = useMemo(
		() =>
			serviceAccounts
				?.filter((user) => user.service_account_count_popularity)
				?.map((user) => {
					const emailHash = getHash(user.email).toString();
					return emailHash;
				}),
		[serviceAccounts]
	);

	return (
		<Stack spacing="md">
			<Stack spacing={0}>
				<Title size="md">Popularity</Title>
			</Stack>
			<Box>
				<SettingsBlock
					title="Include in popularity metrics"
					description="Select which accounts to include in resource popularity metrics"
					rightComponent={
						<Selector
							initialValue={usersValues ?? []}
							getItems={getFilteredServiceAccountsItems}
							onChange={handlePopularityChange}
							selectType={SelectType.Multiple}
							disableSearch={false}
							disabled={false}
						>
							<SelectorTarget variant="tertiary" className={classes.button}>
								<Group spacing="2xs">
									<Text size="sm">
										{pluralize('account', usersValues?.length, true)}
									</Text>
								</Group>
							</SelectorTarget>
						</Selector>
					}
				/>
				<SettingsSelect
					title="Popularity lookback window"
					description="Queries from this time range will contribute to popularity scores"
					options={LOOKBACK_OPTIONS}
					value={String(lookbackDays)}
					onChange={(value) => handlePopularityLookbackChange(value ?? '7')}
				/>
			</Box>
		</Stack>
	);
}
